import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, PaperPlaneTilt, FloppyDisk, Eye, Sparkle } from "@phosphor-icons/react";
import AIComposeModal from "@/components/admin/AIComposeModal";
import AutoStaggerPanel from "@/components/admin/AutoStaggerPanel";

const EMPTY = {
    name: "", subject: "",
    from_name: "Surveys2CAD", from_email: "surveys2cad@gmail.com",
    html_body: "", text_body: "",
    template_id: null,
    recipient_contact_ids: [],
    tags_filter: [],
    manual_emails: [],
    scheduled_at: null,
};

/** Pick the recipient-selection mode from a loaded campaign record. */
function pickRecipientMode(campaign) {
    if (campaign?.manual_emails?.length) return "manual";
    if (campaign?.recipient_contact_ids?.length) return "select";
    if (campaign?.tags_filter?.length) return "tag";
    return "all";
}

const EMAIL_RE = /^[\w.+\-]+@[\w-]+(?:\.[\w-]+)+$/;

export default function CampaignComposer() {
    const { id } = useParams();
    const isNew = !id || id === "new";
    const [params] = useSearchParams();
    const preselectTemplate = params.get("template");
    const nav = useNavigate();

    const [c, setC] = useState(EMPTY);
    const [templates, setTemplates] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [tagFilter, setTagFilter] = useState("");
    const [selectedTags, setSelectedTags] = useState([]);
    const [recipientMode, setRecipientMode] = useState("all"); // "all" | "tag" | "select" | "state_batch" | "manual"
    const [selectedIds, setSelectedIds] = useState([]);
    const [batchStates, setBatchStates] = useState([]);      // states selected for state_batch mode
    const [batchNumber, setBatchNumber] = useState(1);        // 1-indexed; slice (batch-1)*500 .. batch*500
    const [manualEmailsRaw, setManualEmailsRaw] = useState(""); // free-form textarea text
    const [previewOpen, setPreviewOpen] = useState(false);
    const [testEmail, setTestEmail] = useState("");
    const [saving, setSaving] = useState(false);

    // AI HTML generator (Claude Sonnet 4.5)
    const [aiOpen, setAiOpen] = useState(false);
    const [aiTopic, setAiTopic] = useState("");
    const [aiKeywords, setAiKeywords] = useState("");
    const [aiAudience, setAiAudience] = useState("land surveyors and civil engineering firms");
    const [aiTone, setAiTone] = useState("professional");
    const [aiCta, setAiCta] = useState("Reply to learn more");
    const [aiColor, setAiColor] = useState("#06b6d4");
    const [aiLength, setAiLength] = useState("medium");
    const [aiBusy, setAiBusy] = useState(false);

    // Auto-stagger (split big lists into 500/day scheduled batches)
    const [staggerOpen, setStaggerOpen] = useState(false);
    const [staggerStart, setStaggerStart] = useState("");
    const [staggerBatchSize, setStaggerBatchSize] = useState(500);
    const [staggerHours, setStaggerHours] = useState(24);
    const [staggerStates, setStaggerStates] = useState([]);
    const [staggerTags, setStaggerTags] = useState([]);
    const [staggerBusy, setStaggerBusy] = useState(false);
    const [staggerResult, setStaggerResult] = useState(null);

    useEffect(() => {
        document.title = "Surveys2CAD | Campaign Composer";
        api.get("/admin/templates").then((r) => setTemplates(r.data.items));
        api.get("/admin/contacts", { params: { limit: 5000, sort: "state" } }).then((r) => setContacts(r.data.items));
        if (!isNew) {
            api.get(`/admin/campaigns/${id}`).then((r) => {
                setC(r.data);
                setSelectedIds(r.data.recipient_contact_ids || []);
                setSelectedTags(r.data.tags_filter || []);
                setManualEmailsRaw((r.data.manual_emails || []).join("\n"));
                setRecipientMode(pickRecipientMode(r.data));
            });
        } else if (preselectTemplate) {
            api.get(`/admin/templates/${preselectTemplate}`).then((r) => {
                setC((prev) => ({
                    ...prev,
                    template_id: r.data.id,
                    name: `Campaign — ${r.data.name}`,
                    subject: r.data.subject,
                    html_body: r.data.html_body,
                    text_body: r.data.text_body || "",
                }));
            });
        }
    }, [id, isNew, preselectTemplate]); // api + setters are stable; id/isNew/preselectTemplate are the only externally-changing deps.

    const allTags = useMemo(() => {
        const s = new Set();
        contacts.forEach((ct) => (ct.tags || []).forEach((t) => s.add(t)));
        return Array.from(s).sort();
    }, [contacts]);

    const BATCH_CAP = 500;

    // Group active contacts by state for state-batch recipient mode.
    const contactsByState = useMemo(() => {
        const out = {};
        contacts
            .filter((x) => x.status === "active")
            .forEach((x) => {
                const k = x.state || "Unknown";
                (out[k] = out[k] || []).push(x);
            });
        // Sort each bucket deterministically (by county then name) so batches
        // are stable across re-renders.
        Object.values(out).forEach((arr) => {
            arr.sort((a, b) => {
                const byCounty = (a.county || "").localeCompare(b.county || "");
                if (byCounty !== 0) return byCounty;
                return (a.name || "").localeCompare(b.name || "");
            });
        });
        return out;
    }, [contacts]);

    const availableStates = useMemo(
        () => Object.keys(contactsByState).sort((a, b) => contactsByState[b].length - contactsByState[a].length),
        [contactsByState],
    );

    // For each chosen state, compute how many 500-sized batches exist.
    const maxBatchesAcrossStates = useMemo(() => {
        if (!batchStates.length) return 1;
        return Math.max(
            1,
            ...batchStates.map((st) => Math.ceil((contactsByState[st]?.length || 0) / BATCH_CAP)),
        );
    }, [batchStates, contactsByState]);

    // Resolve the recipient IDs for the current (states × batchNumber) slice,
    // capped at BATCH_CAP total to respect the Gmail per-send limit.
    const stateBatchIds = useMemo(() => {
        if (recipientMode !== "state_batch" || !batchStates.length) return [];
        const ids = [];
        for (const st of batchStates) {
            const pool = contactsByState[st] || [];
            const start = (batchNumber - 1) * BATCH_CAP;
            const slice = pool.slice(start, start + BATCH_CAP);
            for (const x of slice) {
                ids.push(x.id);
                if (ids.length >= BATCH_CAP) break;
            }
            if (ids.length >= BATCH_CAP) break;
        }
        return ids;
    }, [recipientMode, batchStates, batchNumber, contactsByState]);

    // Per-state contribution to the current batch (for UI preview)
    const stateBatchBreakdown = useMemo(() => {
        if (recipientMode !== "state_batch") return {};
        const out = {};
        let remaining = BATCH_CAP;
        for (const st of batchStates) {
            if (remaining <= 0) { out[st] = 0; continue; }
            const pool = contactsByState[st] || [];
            const start = (batchNumber - 1) * BATCH_CAP;
            const take = Math.min(remaining, Math.max(0, Math.min(pool.length - start, BATCH_CAP)));
            out[st] = take;
            remaining -= take;
        }
        return out;
    }, [recipientMode, batchStates, batchNumber, contactsByState]);

    const manualEmailsList = useMemo(() => {
        // Split by newline/comma/semicolon, trim, validate, dedupe.
        const tokens = manualEmailsRaw
            .split(/[\s,;]+/)
            .map((t) => t.trim())
            .filter(Boolean);
        const valid = [];
        const invalid = [];
        const seen = new Set();
        for (const t of tokens) {
            if (!EMAIL_RE.test(t)) { invalid.push(t); continue; }
            const key = t.toLowerCase();
            if (seen.has(key)) continue;
            seen.add(key);
            valid.push(t);
        }
        return { valid, invalid, total: tokens.length };
    }, [manualEmailsRaw]);

    const recipientCount = useMemo(() => {
        if (recipientMode === "manual") {
            return manualEmailsList.valid.length;
        }
        if (recipientMode === "all") {
            return contacts.filter((x) => x.status === "active").length;
        }
        if (recipientMode === "tag") {
            return contacts.filter(
                (x) => x.status === "active" && selectedTags.some((t) => (x.tags || []).includes(t))
            ).length;
        }
        if (recipientMode === "state_batch") {
            return stateBatchIds.length;
        }
        return selectedIds.length;
    }, [contacts, recipientMode, selectedTags, selectedIds, stateBatchIds, manualEmailsList]);

    /** Memoized first-100 filtered contacts for the "select" recipient picker. */
    const selectablePreview = useMemo(() => {
        const q = tagFilter.trim().toLowerCase();
        const filtered = q
            ? contacts.filter(
                  (x) =>
                      x.name?.toLowerCase().includes(q) ||
                      x.email?.toLowerCase().includes(q)
              )
            : contacts;
        return filtered.slice(0, 100);
    }, [contacts, tagFilter]);

    const applyTemplate = async (tplId) => {
        if (!tplId) return;
        const { data } = await api.get(`/admin/templates/${tplId}`);
        setC({ ...c, template_id: data.id, subject: data.subject, html_body: data.html_body, text_body: data.text_body || "" });
        toast.success(`Loaded template: ${data.name}`);
    };

    const buildPayload = () => {
        const useStateBatch = recipientMode === "state_batch";
        const useSelect = recipientMode === "select";
        const useManual = recipientMode === "manual";
        return {
            name: c.name,
            subject: c.subject,
            from_name: c.from_name,
            from_email: c.from_email,
            html_body: c.html_body,
            text_body: c.text_body,
            template_id: c.template_id,
            recipient_contact_ids: useStateBatch ? stateBatchIds : (useSelect ? selectedIds : []),
            tags_filter: recipientMode === "tag" ? selectedTags : [],
            manual_emails: useManual ? manualEmailsList.valid : [],
            scheduled_at: c.scheduled_at || null,
        };
    };

    const save = async () => {
        setSaving(true);
        try {
            if (isNew) {
                const { data } = await api.post("/admin/campaigns", buildPayload());
                toast.success("Draft saved");
                nav(`/admin/campaigns/${data.id}`);
            } else {
                await api.put(`/admin/campaigns/${id}`, buildPayload());
                toast.success("Saved");
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    const sendNow = async () => {
        if (!window.confirm(`Send now to ${recipientCount} recipient${recipientCount === 1 ? "" : "s"}?`)) return;
        try {
            // Save first
            let cid = id;
            if (isNew) {
                const { data } = await api.post("/admin/campaigns", buildPayload());
                cid = data.id;
            } else {
                await api.put(`/admin/campaigns/${id}`, buildPayload());
            }
            await api.post(`/admin/campaigns/${cid}/send`);
            toast.success("Campaign queued — analytics updating in real-time");
            nav(`/admin/campaigns/${cid}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Send failed");
        }
    };

    const sendTest = async () => {
        if (!testEmail) return toast.error("Enter a test email first");
        if (!c.subject?.trim() || !c.html_body?.trim()) {
            return toast.error("Add a subject and HTML body before sending a test");
        }
        try {
            let cid = id;
            if (isNew) {
                const { data } = await api.post("/admin/campaigns", buildPayload());
                cid = data.id;
                nav(`/admin/campaigns/${cid}`, { replace: true });
            } else {
                await api.put(`/admin/campaigns/${id}`, buildPayload());
            }
            await api.post(`/admin/campaigns/${cid}/send`, { test_email: testEmail });
            toast.success(`Test sent to ${testEmail}`);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = Array.isArray(detail) ? detail.map((d) => d.msg || d).join("; ") : detail;
            toast.error(msg || err?.message || "Test send failed — check SMTP config");
        }
    };

    const generateAiEmail = async () => {
        if (!aiTopic.trim()) return toast.error("Describe the email topic first");
        setAiBusy(true);
        try {
            const { data } = await api.post("/admin/ai/email-html", {
                topic: aiTopic,
                audience: aiAudience,
                tone: aiTone,
                cta: aiCta,
                primary_color: aiColor,
                length: aiLength,
                keywords: aiKeywords.split(",").map((s) => s.trim()).filter(Boolean),
            }, { timeout: 60000 });
            setC((prev) => ({
                ...prev,
                subject: data.subject,
                html_body: data.html_body,
                text_body: data.text_body || prev.text_body,
            }));
            toast.success(`Generated · ${data.html_body.length} chars · ${data.model || "claude-sonnet-4-5"}`);
            setAiOpen(false);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "AI generation failed");
        } finally { setAiBusy(false); }
    };

    const runAutoStagger = async () => {
        if (!staggerStart) return toast.error("Pick a start date/time");
        if (!c.subject || !c.html_body) return toast.error("Fill in subject + HTML body first");
        setStaggerBusy(true);
        setStaggerResult(null);
        try {
            // Save the parent campaign first so children copy the latest content.
            let cid = id;
            if (isNew) {
                const { data } = await api.post("/admin/campaigns", buildPayload());
                cid = data.id;
            } else {
                await api.put(`/admin/campaigns/${id}`, buildPayload());
            }
            const startISO = new Date(staggerStart).toISOString();
            const { data } = await api.post(`/admin/campaigns/${cid}/auto-stagger`, {
                start_at: startISO,
                batch_size: Number(staggerBatchSize) || 500,
                hours_between_batches: Number(staggerHours) || 24,
                states: staggerStates,
                tags: staggerTags,
            });
            setStaggerResult(data);
            toast.success(
                `${data.batches_created} batch${data.batches_created === 1 ? "" : "es"} scheduled · ` +
                `${data.total_recipients} contacts across ${data.batches_created} day${data.batches_created === 1 ? "" : "s"}`,
            );
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Auto-stagger failed");
        } finally { setStaggerBusy(false); }
    };

    return (
        <div className="p-4 sm:p-6 lg:p-8" data-testid="campaign-composer">
            <button onClick={() => nav("/admin/campaigns")} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-300 mb-4">
                <ArrowLeft size={16} /> Campaigns
            </button>

            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                <div>
                    <div className="overline mb-2">{isNew ? "New" : "Edit"} campaign</div>
                    <h1 className="font-heading text-3xl font-bold text-slate-50 tracking-tight">
                        {c.name || "Untitled campaign"}
                    </h1>
                    <p className="text-slate-400 mt-1 font-mono-alt text-xs">
                        Recipients: <span className="text-cyan-300">{recipientCount}</span>
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => setAiOpen(true)}
                        className="btn-ghost !border-purple-500/40 !text-purple-200 hover:!bg-purple-500/10"
                        data-testid="ai-compose-btn"
                        title="Generate HTML email from a brief using Claude Sonnet 4.5"
                    >
                        <Sparkle size={16} weight="fill" /> AI Compose
                    </button>
                    <button onClick={() => setPreviewOpen(true)} className="btn-ghost"><Eye size={16} /> Preview</button>
                    <button onClick={save} disabled={saving} className="btn-ghost" data-testid="campaign-save-btn">
                        <FloppyDisk size={16} /> Save draft
                    </button>
                    <button onClick={sendNow} disabled={recipientCount === 0} className="btn-primary" data-testid="campaign-send-btn">
                        <PaperPlaneTilt size={16} weight="bold" /> Send now
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main content */}
                <div className="lg:col-span-2 space-y-4">
                    <div className="tech-card p-6 space-y-4">
                        <div>
                            <label className="overline mb-2 block">Campaign name (internal)</label>
                            <input type="text" value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })}
                                className="w-full bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-cyan-500"
                                data-testid="cc-name" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="overline mb-2 block">From name</label>
                                <input type="text" value={c.from_name} onChange={(e) => setC({ ...c, from_name: e.target.value })}
                                    className="w-full bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-cyan-500" />
                            </div>
                            <div>
                                <label className="overline mb-2 block">From email</label>
                                <input type="email" value={c.from_email} onChange={(e) => setC({ ...c, from_email: e.target.value })}
                                    className="w-full bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-cyan-500" />
                            </div>
                        </div>
                        <div>
                            <label className="overline mb-2 block">Subject line</label>
                            <input type="text" value={c.subject} onChange={(e) => setC({ ...c, subject: e.target.value })}
                                className="w-full bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-cyan-500"
                                data-testid="cc-subject" />
                        </div>
                        <div>
                            <label className="overline mb-2 block">HTML body</label>
                            <textarea rows={16} value={c.html_body} onChange={(e) => setC({ ...c, html_body: e.target.value })}
                                className="w-full bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-cyan-500 font-mono-alt text-xs resize-y"
                                data-testid="cc-html" />
                        </div>
                        <div>
                            <label className="overline mb-2 block">Plain text (optional)</label>
                            <textarea rows={4} value={c.text_body || ""} onChange={(e) => setC({ ...c, text_body: e.target.value })}
                                className="w-full bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-cyan-500 resize-y" />
                        </div>
                    </div>
                </div>

                {/* Side panel */}
                <div className="space-y-4">
                    <div className="tech-card p-5">
                        <div className="overline mb-3">Load a template</div>
                        <select onChange={(e) => applyTemplate(e.target.value)} value={c.template_id || ""}
                            className="w-full bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-cyan-500"
                            data-testid="cc-template-select">
                            <option value="">— Select template —</option>
                            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                    </div>

                    <div className="tech-card p-5">
                        <div className="overline mb-3">Recipients</div>
                        <div className="space-y-2 text-sm">
                            {[
                                { k: "all", label: "All active contacts" },
                                { k: "tag", label: "Filter by tags" },
                                { k: "select", label: "Select individually" },
                                { k: "state_batch", label: "By state (batches of 500)" },
                                { k: "manual", label: "Manual emails (paste / type)" },
                            ].map((m) => (
                                <label key={m.k} className="flex items-center gap-2 cursor-pointer text-slate-300">
                                    <input type="radio" name="rmode" value={m.k} checked={recipientMode === m.k}
                                        onChange={() => setRecipientMode(m.k)} className="accent-cyan-500"
                                        data-testid={`rmode-${m.k}`} />
                                    {m.label}
                                </label>
                            ))}
                        </div>

                        {recipientMode === "tag" && (
                            <div className="mt-4">
                                <div className="overline mb-2">Tags</div>
                                <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                                    {allTags.length === 0 && <span className="text-xs text-slate-500">No tags yet. Add tags to contacts first.</span>}
                                    {allTags.map((t) => {
                                        const active = selectedTags.includes(t);
                                        return (
                                            <button key={t} type="button"
                                                onClick={() => setSelectedTags(active ? selectedTags.filter((x) => x !== t) : [...selectedTags, t])}
                                                className={`px-2 py-1 rounded-full text-[11px] font-mono-alt ${
                                                    active ? "bg-cyan-500/20 text-cyan-200 border border-cyan-500/50"
                                                           : "bg-slate-800 text-slate-300 border border-slate-700"
                                                }`}>
                                                {t}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {recipientMode === "select" && (
                            <div className="mt-4">
                                <input type="text" placeholder="Filter…" value={tagFilter}
                                    onChange={(e) => setTagFilter(e.target.value)}
                                    className="w-full bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 text-xs mb-2" />
                                <div className="max-h-48 overflow-y-auto border border-slate-800 rounded-lg divide-y divide-slate-800">
                                    {selectablePreview.map((x) => {
                                        const checked = selectedIds.includes(x.id);
                                        return (
                                            <label key={x.id} className="flex items-center gap-2 p-2 cursor-pointer hover:bg-slate-900/50">
                                                <input type="checkbox" checked={checked} className="accent-cyan-500"
                                                    onChange={() => setSelectedIds(checked ? selectedIds.filter((i) => i !== x.id) : [...selectedIds, x.id])} />
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-xs text-slate-100 truncate">{x.name}</div>
                                                    <div className="text-[10px] text-slate-500 truncate">{x.email}</div>
                                                </div>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {recipientMode === "state_batch" && (
                            <div className="mt-4 space-y-3" data-testid="rmode-state-batch-panel">
                                <p className="text-[11px] text-slate-400 leading-relaxed">
                                    Pick one or more states. Each batch sends to up to <span className="text-cyan-300 font-mono-alt">500</span> contacts
                                    (Gmail free-tier daily limit). Re-run the campaign with batch 2, 3, … until every contact receives it.
                                </p>

                                <div>
                                    <div className="overline mb-2">States (active contacts)</div>
                                    {availableStates.length === 0 ? (
                                        <p className="text-xs text-slate-500">No active contacts with a state set. Add contacts or use Lead Finder first.</p>
                                    ) : (
                                        <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
                                            {availableStates.map((st) => {
                                                const total = contactsByState[st].length;
                                                const active = batchStates.includes(st);
                                                const batchesForState = Math.ceil(total / BATCH_CAP);
                                                return (
                                                    <button
                                                        key={st}
                                                        type="button"
                                                        onClick={() =>
                                                            setBatchStates(active
                                                                ? batchStates.filter((x) => x !== st)
                                                                : [...batchStates, st])
                                                        }
                                                        className={`px-2.5 py-1 rounded-full text-[11px] font-mono-alt border transition-colors ${
                                                            active
                                                                ? "bg-orange-500/20 border-orange-400/60 text-orange-100"
                                                                : "bg-slate-900 border-slate-800 text-slate-400 hover:text-slate-200"
                                                        }`}
                                                        data-testid={`rmode-state-${st}`}
                                                        title={`${total} contacts · ${batchesForState} batch${batchesForState === 1 ? "" : "es"}`}
                                                    >
                                                        {st} · {total}{batchesForState > 1 ? ` (${batchesForState}×)` : ""}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {batchStates.length > 0 && (
                                    <>
                                        <div>
                                            <div className="overline mb-2">Batch number</div>
                                            <div className="flex items-center gap-2 flex-wrap">
                                                {Array.from({ length: maxBatchesAcrossStates }, (_, i) => i + 1).map((n) => (
                                                    <button
                                                        key={n}
                                                        type="button"
                                                        onClick={() => setBatchNumber(n)}
                                                        className={`min-w-[36px] px-2.5 py-1 rounded-md text-xs font-mono-alt border ${
                                                            batchNumber === n
                                                                ? "bg-cyan-500/20 border-cyan-400/60 text-cyan-100"
                                                                : "bg-slate-900 border-slate-800 text-slate-400 hover:text-slate-100"
                                                        }`}
                                                        data-testid={`rmode-batch-${n}`}
                                                    >
                                                        #{n}
                                                    </button>
                                                ))}
                                            </div>
                                            <p className="text-[10px] text-slate-500 mt-2 font-mono-alt">
                                                Batch <span className="text-cyan-300">#{batchNumber}</span> = contacts{" "}
                                                {(batchNumber - 1) * BATCH_CAP + 1}–{batchNumber * BATCH_CAP} per state
                                            </p>
                                        </div>

                                        <div className="bg-slate-950/60 border border-slate-800 rounded-lg p-3 space-y-1" data-testid="rmode-state-batch-breakdown">
                                            <div className="overline text-[10px]">This batch</div>
                                            {Object.entries(stateBatchBreakdown).map(([st, n]) => (
                                                <div key={st} className="flex justify-between text-[11px] font-mono-alt">
                                                    <span className="text-slate-400">{st}</span>
                                                    <span className={n > 0 ? "text-cyan-300" : "text-slate-600"}>{n} contact{n === 1 ? "" : "s"}</span>
                                                </div>
                                            ))}
                                            {stateBatchIds.length === BATCH_CAP && (
                                                <p className="text-[10px] text-amber-300 mt-2">
                                                    ⚠ Batch is full (500). Any further states/contacts are skipped — send this batch first, then run another.
                                                </p>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {recipientMode === "manual" && (
                            <div className="mt-4 space-y-2" data-testid="rmode-manual-panel">
                                <p className="text-[11px] text-slate-400 leading-relaxed">
                                    Paste or type emails — one per line, comma, or semicolon. Sends to the addresses below
                                    without saving them as contacts. Combine with state batches by switching modes after testing.
                                </p>
                                <textarea
                                    rows={6}
                                    placeholder={"john@firm.com\nmaria@surveyors.io\nadmin@company.net, sales@firm.us"}
                                    value={manualEmailsRaw}
                                    onChange={(e) => setManualEmailsRaw(e.target.value)}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 text-xs font-mono-alt focus:outline-none focus:border-cyan-500 resize-y"
                                    data-testid="rmode-manual-textarea"
                                />
                                <div className="flex items-center justify-between text-[11px] font-mono-alt">
                                    <span className="text-cyan-300" data-testid="rmode-manual-valid-count">
                                        ✓ {manualEmailsList.valid.length} valid
                                    </span>
                                    {manualEmailsList.invalid.length > 0 && (
                                        <span
                                            className="text-amber-300"
                                            title={manualEmailsList.invalid.slice(0, 8).join(", ")}
                                            data-testid="rmode-manual-invalid-count"
                                        >
                                            ⚠ {manualEmailsList.invalid.length} invalid (skipped)
                                        </span>
                                    )}
                                </div>
                                {manualEmailsList.valid.length > 0 && (
                                    <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto bg-slate-950/60 border border-slate-800 rounded p-2">
                                        {manualEmailsList.valid.slice(0, 20).map((e) => (
                                            <span key={e} className="px-1.5 py-0.5 rounded bg-cyan-500/10 border border-cyan-500/30 text-cyan-200 text-[10px] font-mono-alt">
                                                {e}
                                            </span>
                                        ))}
                                        {manualEmailsList.valid.length > 20 && (
                                            <span className="text-[10px] text-slate-500 self-center">
                                                + {manualEmailsList.valid.length - 20} more
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="mt-4 pt-4 border-t border-slate-800 text-xs">
                            <span className="text-slate-500">Will send to </span>
                            <span className="font-heading font-bold text-cyan-300">{recipientCount}</span>
                            <span className="text-slate-500"> recipients</span>
                        </div>
                    </div>

                    <div className="tech-card p-5">
                        <div className="overline mb-3">Schedule</div>
                        <input type="datetime-local" value={c.scheduled_at ? String(c.scheduled_at).slice(0, 16) : ""}
                            onChange={(e) => setC({ ...c, scheduled_at: e.target.value || null })}
                            className="w-full bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 text-sm" />
                        <p className="text-[11px] text-slate-500 mt-2">
                            Leave blank to send manually. Scheduled campaigns will auto-fire at the selected time.
                        </p>

                        <AutoStaggerPanel
                            open={staggerOpen}
                            onToggle={() => setStaggerOpen((v) => !v)}
                            start={staggerStart} setStart={setStaggerStart}
                            batchSize={staggerBatchSize} setBatchSize={setStaggerBatchSize}
                            hours={staggerHours} setHours={setStaggerHours}
                            states={staggerStates}
                            toggleState={(st) => setStaggerStates(staggerStates.includes(st)
                                ? staggerStates.filter((x) => x !== st)
                                : [...staggerStates, st])}
                            availableStates={availableStates}
                            busy={staggerBusy}
                            onSubmit={runAutoStagger}
                            result={staggerResult}
                        />
                    </div>

                    <div className="tech-card p-5">
                        <div className="overline mb-3">Test send</div>
                        <div className="flex gap-2">
                            <input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
                                placeholder="you@email.com"
                                className="flex-1 bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 text-sm"
                                data-testid="cc-test-email" />
                            <button type="button" onClick={sendTest} className="btn-ghost !py-2 text-xs whitespace-nowrap" data-testid="cc-test-send">
                                Send
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {previewOpen && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="tech-card w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between p-5 border-b border-slate-800">
                            <div>
                                <div className="text-xs text-slate-500">From: {c.from_name} &lt;{c.from_email}&gt;</div>
                                <div className="font-heading text-lg font-semibold text-slate-50 mt-1">{c.subject || "(no subject)"}</div>
                            </div>
                            <button onClick={() => setPreviewOpen(false)} className="text-slate-400 hover:text-slate-100 text-2xl">×</button>
                        </div>
                        <iframe title="preview" srcDoc={c.html_body} sandbox="" className="w-full flex-1 bg-white min-h-[65vh]" />
                    </div>
                </div>
            )}

            {aiOpen && (
                <AIComposeModal
                    open={aiOpen}
                    onClose={() => setAiOpen(false)}
                    topic={aiTopic} setTopic={setAiTopic}
                    keywords={aiKeywords} setKeywords={setAiKeywords}
                    audience={aiAudience} setAudience={setAiAudience}
                    tone={aiTone} setTone={setAiTone}
                    cta={aiCta} setCta={setAiCta}
                    color={aiColor} setColor={setAiColor}
                    length={aiLength} setLength={setAiLength}
                    busy={aiBusy}
                    onGenerate={generateAiEmail}
                />
            )}
        </div>
    );
}
